import { EditPage, EditPageTextField, EditPageButton, EditPageSelect, EditPagePanel, EditPageTextArea, EditPageCheckbox } from './../../../common/EditPage';
import SelectContainer from '../../../common/SelectContainer';
import { update, getOne } from './../../../common/v4/BackendProvider';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

const AiToolsEdit = () => {
    const [data, setData] = useState({status: true});
    const [metadata, setMetadata] = useState({});
    const [params, setParams] = useState([]);
    const navigate = useNavigate();
    const { tool_id } = useParams();
    useEffect(() => {
        // Load metadata for MessageTemplate and MailAccount models
        getOne('/admin/ai/tools/', 'new').then(response => {
    
            setMetadata(response.data.metadata);
        }).catch(error => {
            console.error('Error loading metadata:', error);
        });
    }, []);

    useEffect(() => {
        // Load the AI tool data
        getOne('/admin/ai/tools/', tool_id).then(response => {
            const toolData = response.data.data;
            setData(toolData);
            
            if (toolData.params) {
                setParams(toolData.params.map(param => ({
                    id: param.id,
                    ai_function_param_id: param.ai_function_param_id,
                    value: param.value,
                    description: param.description,
                    required: param.required,
                    presentation_type: param.presentation_type,
                    agent_provided: param.agent_provided || false
                })));
                
                // Initialize agent_provided flags and descriptions in data
                const agentFlags = {};
                const descriptions = {};
                toolData.params.forEach(param => {
                    agentFlags[`${param.ai_function_param_id}_agent_provided`] = param.agent_provided || false;
                    descriptions[`${param.ai_function_param_id}_description`] = param.description || '';
                });
                
                // Set the params in data so the UI can access them
                const dataParams = toolData.params.map(param => ({
                    id: param.id,
                    ai_function_param_id: param.ai_function_param_id,
                    value: param.value,
                    description: param.description,
                    required: param.required,
                    presentation_type: param.presentation_type,
                    agent_provided: param.agent_provided || false
                }));
                
                setData(prevData => ({
                    ...prevData,
                    params: dataParams,
                    ...agentFlags,
                    ...descriptions
                }));
            }
        }).catch(error => {
            console.error('Error loading tool data:', error);
        });
    }, [tool_id]);


    useEffect(() => {
        if (data.function_id && Object.keys(metadata).length > 0) {
            getOne('/admin/ai/functions/', data.function_id).then(response => {
                const functionParams = response.data.data.params;
                const updatedParams = functionParams.map(param => {
                    const updatedParam = { ...param };
                    
                    if (param.type && param.type.startsWith('model:MessageTemplate|channel=')) {
                        const channel = param.type.split('=')[1]; 
                        if (channel === 'email' && metadata.message_templates_email) {
                            updatedParam.options = metadata.message_templates_email;
                        } else if (channel === 'whatsapp' && metadata.message_templates_whatsapp) {
                            updatedParam.options = metadata.message_templates_whatsapp;
                        } else if (channel === 'sms' && metadata.message_templates_sms) {
                            updatedParam.options = metadata.message_templates_sms;
                        }
                    } else if (param.type && param.type.startsWith('model:MailAccount')) {
                        updatedParam.options = metadata.email_accounts;
                    }
                    return updatedParam;
                });

                const functionParamsWithOptions = updatedParams;
                
                // Update existing params with new options while preserving current values
                setParams(prevParams => {
                    
                    const newParams = prevParams.map(existingParam => {
                        
                        // Find the function parameter that matches this tool parameter
                        const functionParam = functionParamsWithOptions.find(p => p.id === existingParam.ai_function_param_id);
                        
                        if (functionParam && functionParam.options) {
                            const updatedParam = {
                                ...existingParam,
                                options: functionParam.options,
                                presentation_type: functionParam.presentation_type,
                                required: functionParam.required,
                                description: functionParam.description
                            };
                            
                            return updatedParam;
                        }
                        
                        return existingParam;
                    });
                    
                    // Also update data.params so the UI can access the options
                    // We need to merge the new params (with options) with the existing data.params (with values)
                    setData(prevData => {
                        const existingParams = prevData.params || [];
                        
                        const mergedParams = newParams.map(newParam => {
                            // Find the existing param to preserve the value
                            const existingParam = existingParams.find(ep => ep.id === newParam.id);
                            
                            return {
                                ...newParam,
                                value: existingParam ? existingParam.value : newParam.value || ''
                            };
                        });
                        
                        return {
                            ...prevData,
                            params: mergedParams
                        };
                    });
                    
                    return newParams;
                });
            }).catch(error => {
                toast.error("Error loading function parameters: " + error.message);
            });
        }
    }, [data.function_id, metadata]);


    const handleSave = () => {
        // Validate that parameter descriptions are provided only when AI Agent is checked
        const missingDescriptions = data.params.filter(param => {
            const isAgentProvided = data[`${param.ai_function_param_id}_agent_provided`] || false;
            const hasDescription = data[`${param.ai_function_param_id}_description`] && data[`${param.ai_function_param_id}_description`].trim() !== '';
            return isAgentProvided && !hasDescription;
        });
        
        if (missingDescriptions.length > 0) {
            const paramNames = missingDescriptions.map(param => param.description || `Parameter ${param.ai_function_param_id}`).join(', ');
            toast.error(`Please provide descriptions for parameters that will be filled by AI Agent: ${paramNames}`);
            return;
        }
        
        // Prepare the data to send, including agent_provided flags and descriptions
        const saveData = {
            ...data,
            agent_provided_flags: data.params.reduce((acc, param) => {
                acc[param.ai_function_param_id] = data[`${param.ai_function_param_id}_agent_provided`] || false;
                return acc;
            }, {}),
            param_descriptions: data.params.reduce((acc, param) => {
                acc[param.ai_function_param_id] = data[`${param.ai_function_param_id}_description`] || '';
                return acc;
            }, {})
        };
        
        update('/admin/ai/tools/', tool_id, {data: saveData}).then(response => {
            toast.success("AI Tool updated successfully", {});
            navigate('/ai/tools');
        }).catch(error => {
            toast.error("Error updating AI Tool: " + error.message, {});
        });
    }

    const handleCancel = () => {
        navigate('/ai/tools');
    }

    const onChange = (id, value) => {
        setData({ ...data, [id]: value });
    }

    const onParamChange = (id, value) => {
        setParams(prevParams => {
            const newParams = prevParams.map(param => 
                param.id === id ? { ...param, value } : param
            );
            setData(prevData => ({
                ...prevData,
                params: newParams.map(param => ({
                    id: param.id,
                    ai_function_param_id: param.ai_function_param_id,
                    value: param.value,
                    description: param.description,
                    required: param.required,
                    presentation_type: param.presentation_type,
                    options: param.options  // Preserve the options!
                }))
            }));
            return newParams;
        });
    }

    return (
        <div>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                closeOnClick={true}
                pauseOnHover={true}
                draggable={true}
                progress="undefined"
            />
            <EditPage handleSave={ handleSave } handleCancel={ handleCancel } title={`Edit AI Tool ${data.name}`} >
                <EditPagePanel className="col-lg-6">
                    <EditPageTextField 
                        label="Name (Required, lowercase letters, numbers, and underscores)" 
                        id="name" 
                        onChange={ onChange } 
                        value={ data.name } 
                        pattern="[a-z0-9_]+"
                        placeholder="e.g., email_sender, sms_notifier, data_processor"
                    />
                    <EditPageTextField label="Description (Required, passed to AI Agent)" id="description" onChange={ onChange } value={ data.description } />
                    <EditPageTextField label="Function" id="function_id" onChange={ onChange } value={ data.function?.name } disabled={ true } />
                    <EditPageCheckbox label="Is Active" id="status" onChange={ onChange } value={ data.status } />
                </EditPagePanel>
                <EditPagePanel className="col-lg-6">             
                    {data.params &&
                        <>
                            {data.params.map(param => (
                                <div key={param.id} style={{ marginBottom: '20px', padding: '15px', border: '1px solid #e9ecef', borderRadius: '5px' }}>
                                    {(() => {
                                        if (param.presentation_type === 'textarea') {
                                            return (
                                                <EditPageTextArea 
                                                    label={param.required ? param.description + " (Required)" : param.description} 
                                                    id={param.id} 
                                                    onChange={onParamChange} 
                                                    value={param.value} 
                                                    disabled={data[`${param.ai_function_param_id}_agent_provided`] || false}
                                                />
                                            );
                                        } else if (param.presentation_type === 'select') {
                                            return (
                                                <SelectContainer 
                                                    label={param.required ? param.description + " (Required)" : param.description}
                                                    id={param.id}
                                                    onChange={onParamChange}
                                                    value={param.value}
                                                    options={param.options || []}
                                                    disabled={data[`${param.ai_function_param_id}_agent_provided`] || false}
                                                />
                                            );
                                        } else {
                                            return (
                                                <EditPageTextField 
                                                    label={param.required ? param.description + " (Required)" : param.description} 
                                                    id={param.id} 
                                                    onChange={onParamChange} 
                                                    value={param.value} 
                                                    disabled={data[`${param.ai_function_param_id}_agent_provided`] || false}
                                                />
                                            );
                                        }
                                    })()}
                                    <EditPageCheckbox 
                                        label="Fill by AI Agent" 
                                        id={`${param.ai_function_param_id}_agent_provided`} 
                                        onChange={onChange} 
                                        value={data[`${param.ai_function_param_id}_agent_provided`] || false} 
                                    />
                                    {(data[`${param.ai_function_param_id}_agent_provided`] || false) && (
                                        <div style={{ 
                                            fontSize: '12px', 
                                            color: '#6c757d', 
                                            fontStyle: 'italic',
                                            marginTop: '5px',
                                            marginBottom: '10px'
                                        }}>
                                            This parameter will be filled automatically by the AI agent
                                        </div>
                                    )}
                                    <EditPageTextField 
                                        label="Parameter Description (Required when AI Agent is checked)" 
                                        id={`${param.ai_function_param_id}_description`} 
                                        onChange={onChange} 
                                        value={data[`${param.ai_function_param_id}_description`] || ''} 
                                        placeholder="Description for this parameter"
                                        required={data[`${param.ai_function_param_id}_agent_provided`] || false}
                                        disabled={!(data[`${param.ai_function_param_id}_agent_provided`] || false)}
                                    />
                                </div>
                            ))}
                            <div dangerouslySetInnerHTML={{ __html: `Additional fields: {contact.name} {contact.email}` }} />
                        </>
                    || <div>No parameters</div>}
                    <div></div>
                </EditPagePanel>
            </EditPage>
        </div>
    )
}

export default AiToolsEdit;